import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Barcode as ScanBarcode, ChartPie as PieChart, Package as Boxes, Wallet as WalletCards, UsersThree as UsersRound, EnvelopeSimple as MailCheck, Truck, Money as Banknote, WifiSlash as WifiOff, GitBranch, Devices as TabletSmartphone, Printer as PrinterCheck, Key as KeyRound, Wrench } from '@phosphor-icons/react';
import LandingNav from './LandingNav';
import LandingFooter from './LandingFooter';
import SEO from '../../components/SEO';

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { node.classList.add('revealed'); obs.unobserve(node); } },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );
    obs.observe(node);
    return () => obs.unobserve(node);
  }, []);
  return ref;
};

const R = ({ children, className = '', delay = 0 }) => {
  const ref = useReveal();
  return <div ref={ref} className={`reveal-on-scroll ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
};

const screenshots = {
  dashboard: '/images/screenshots/dashboard.png',
  pos: '/images/screenshots/pos.png',
  expenses: '/images/screenshots/expenses.png',
  inventory: '/images/screenshots/inventory.png',
  staff: '/images/screenshots/staff.png',
};

const FeaturesPage = () => {
  const mainFeatures = [
    {
      id: 'pos',
      icon: ScanBarcode,
      accent: 'bg-emerald-500/[0.08]',
      iconColor: 'text-emerald-600',
      title: 'Point of Sale',
      description: 'Fast, intuitive checkout designed for speed and accuracy.',
      image: screenshots.pos,
      highlight: '< 2s per sale',
      highlightLabel: 'Average checkout',
      features: [
        'Quick product search by name, barcode, or SKU',
        'Apply discounts to individual items or entire cart',
        'Split payments across multiple methods',
        'Delivery orders with pay-on-delivery support',
        'Print receipts with your business logo',
        'Hold sales and resume later',
      ],
    },
    {
      id: 'analytics',
      icon: PieChart,
      accent: 'bg-blue-500/[0.08]',
      iconColor: 'text-blue-600',
      title: 'Real-time Analytics',
      description: 'Make data-driven decisions with powerful insights.',
      image: screenshots.dashboard,
      highlight: 'Live data',
      highlightLabel: 'Updated in real-time',
      features: [
        'Track revenue, profit, and expenses in real-time',
        'Compare performance across branches',
        'Staff performance leaderboards',
        'Stock alerts and notifications',
        'Generate detailed reports',
        'Filter by any date range',
      ],
    },
    {
      id: 'inventory',
      icon: Boxes,
      accent: 'bg-amber-500/[0.08]',
      iconColor: 'text-amber-600',
      title: 'Inventory Management',
      description: 'Keep track of every product in every location.',
      image: screenshots.inventory,
      highlight: 'Auto alerts',
      highlightLabel: 'Low stock warnings',
      features: [
        'Track stock levels in real-time',
        'Low stock alerts',
        'Multi-branch inventory',
        'Barcode and SKU support',
        'Stock adjustment history',
        'Inventory reports',
      ],
    },
    {
      id: 'expenses',
      icon: WalletCards,
      accent: 'bg-violet-500/[0.08]',
      iconColor: 'text-violet-600',
      title: 'Expense Tracking',
      description: 'Monitor where your money goes.',
      image: screenshots.expenses,
      highlight: 'Full visibility',
      highlightLabel: 'Every coin tracked',
      features: [
        'Log expenses by category',
        'Visualize spending patterns',
        'Track expenses by branch',
        'Export expense reports',
        'View expense history',
        'Budget alerts',
      ],
    },
    {
      id: 'team',
      icon: UsersRound,
      accent: 'bg-cyan-500/[0.08]',
      iconColor: 'text-cyan-600',
      title: 'Team Management',
      description: 'Control access and track performance.',
      image: screenshots.staff,
      highlight: 'Unlimited',
      highlightLabel: 'Staff accounts',
      features: [
        'Role-based access control (Owner, Manager, Staff)',
        'Unlimited staff accounts',
        'Individual performance tracking',
        'Activity logs',
        'Assign staff to branches',
        'Staff leaderboards',
      ],
    },
  ];

  const additionalFeatures = [
    { icon: MailCheck, title: 'Daily Email Reports', description: 'Automated daily sales summaries delivered to your inbox every night.', accent: 'bg-blue-500/[0.08]', iconColor: 'text-blue-600' },
    { icon: Truck, title: 'Delivery & Out-service', description: 'Handle delivery orders with address tracking, fees, and pay-on-delivery option.', accent: 'bg-amber-500/[0.08]', iconColor: 'text-amber-600' },
    { icon: Banknote, title: 'Split Payments', description: 'Accept multiple payment methods in one transaction – cash, mobile money, and card.', accent: 'bg-emerald-500/[0.08]', iconColor: 'text-emerald-500' },
    { icon: WifiOff, title: 'Works Offline', description: 'Continue selling even without internet. Data syncs automatically when you\'re back online.', accent: 'bg-rose-500/[0.08]', iconColor: 'text-rose-600' },
    { icon: GitBranch, title: 'Multi-Branch', description: 'Manage multiple locations from a single dashboard with consolidated reporting.', accent: 'bg-violet-500/[0.08]', iconColor: 'text-violet-600' },
    { icon: TabletSmartphone, title: 'Mobile Friendly', description: 'Works on any device – phones, tablets, or computers. Install as an app on your device.', accent: 'bg-cyan-500/[0.08]', iconColor: 'text-cyan-600' },
    { icon: PrinterCheck, title: 'Receipt Printing', description: 'Print professional receipts with your logo using Bluetooth or USB printers.', accent: 'bg-orange-500/[0.08]', iconColor: 'text-orange-600' },
    { icon: KeyRound, title: 'Secure & Role-based', description: 'Owner, manager, and staff roles controlling who sees and does what.', accent: 'bg-blue-500/[0.08]', iconColor: 'text-blue-600' },
    { icon: Wrench, title: 'Always Up to Date', description: 'New features and improvements added regularly at no extra cost.', accent: 'bg-teal-500/[0.08]', iconColor: 'text-teal-600' },
  ];

  return (
    <div className="min-h-screen bg-primaryB-50 overflow-x-hidden">
      <SEO
        title="POS Software Features for African Businesses"
        description="Everything your business needs in one POS software: point of sale, inventory management, staff management, expense tracking, real-time analytics, multi-branch support, offline mode, and mobile money payments."
        path="/features"
        keywords="POS software features Africa, inventory management software Africa, pos software Ghana features, pos software Nigeria features, offline POS Africa, multi-branch POS Africa, mobile money POS, business management software Africa"
      />
      <LandingNav />

      {/* ━━━ HERO ━━━ */}
      <section className="pt-24 sm:pt-32 lg:pt-40 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <R>
              <div className="inline-flex items-center gap-2 bg-emerald-500/[0.08] border border-emerald-500/[0.12] rounded-full px-4 py-1.5 mb-6">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                <span className="text-emerald-800 text-xs font-bold tracking-wide">All features included in every plan</span>
              </div>
            </R>
            <R delay={80}>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-primaryB-900 mb-3 leading-tight lg:leading-[1.3]">
                Everything your business needs,{' '}
                <span className="italic text-emerald-600">nothing it doesn't</span>.
              </h1>
            </R>
            <R delay={120}>
              <p className="text-emerald-700 text-sm font-semibold mb-4">
                Complete POS software for African businesses – all features included in every plan.
              </p>
            </R>
            <R delay={160}>
              <p className="text-primaryB-500 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
                No add-ons. No upgrades. One plan gives you every tool to run and grow your business.
              </p>
            </R>
          </div>
        </div>
      </section>

      {/* ━━━ BUSINESS TYPES ━━━ */}
      <section className="py-6 sm:py-10 border-y border-primaryB-200/60 bg-primaryB-50">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-10">
            <span className="text-primaryB-300 text-sm font-semibold whitespace-nowrap shrink-0">Built for</span>
            <div className="flex flex-wrap gap-2">
              {['Retail shops', 'Restaurants & cafes', 'Salons & spas', 'Pharmacies', 'Service businesses'].map((t) => (
                <span key={t} className="px-3.5 py-1.5 sm:px-4 sm:py-2 rounded-full bg-primaryB-50 border border-primaryB-200/60 text-primaryB-700 text-xs sm:text-sm font-semibold">
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ━━━ MAIN FEATURES ━━━ */}
      {mainFeatures.map((feature, index) => {
        const Icon = feature.icon;
        return (
          <section
            key={feature.id}
            className={`py-16 sm:py-24 lg:py-28 ${index % 2 === 0 ? '' : 'bg-primaryB-50 border-y border-primaryB-200/60'}`}
          >
            <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
              <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                {/* Text side */}
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <R>
                    <div className="inline-flex items-center gap-2.5 mb-5">
                      <div className={`w-10 h-10 rounded-xl ${feature.accent} flex items-center justify-center`}>
                        <Icon weight="bold" className={`w-5 h-5 ${feature.iconColor}`} strokeWidth={1.5} />
                      </div>
                      <span className="text-xs font-bold tracking-widest text-primaryB-300 uppercase">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                  </R>
                  <R delay={60}>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primaryB-900 mb-3 tracking-tight">
                      {feature.title}
                    </h2>
                  </R>
                  <R delay={120}>
                    <p className="text-primaryB-500 text-base sm:text-lg leading-relaxed mb-6 sm:mb-8">
                      {feature.description}
                    </p>
                  </R>
                  <R delay={180}>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                      {feature.features.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3 py-1.5">
                          <span className="w-1 h-1 rounded-full bg-emerald-500 flex-shrink-0" />
                          <span className="text-primaryB-700 text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </R>
                </div>

                {/* Screenshot side with floating card */}
                <R delay={100}>
                  <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    {/* Background shape */}
                    <div className="absolute -top-4 -right-4 -bottom-4 -left-4 bg-emerald-800/[0.02] rounded-[2rem] hidden lg:block" style={{ transform: index % 2 === 0 ? 'rotate(-1deg)' : 'rotate(1deg)' }} />

                    {/* Main screenshot */}
                    <div className="relative z-10 bg-primaryB-50 rounded-3xl p-1.5 sm:p-2 shadow-elevated border border-primaryB-200/60">
                      <img
                        src={feature.image}
                        alt={feature.title}
                        className="w-full rounded-xl"
                        loading="lazy"
                      />
                    </div>

                    {/* Floating highlight card */}
                    <div className={`absolute z-20 ${index % 2 === 0 ? '-bottom-3 -left-2 sm:-bottom-4 sm:-left-6' : '-bottom-3 -right-2 sm:-bottom-4 sm:-right-6'} bg-primaryB-50 rounded-2xl shadow-card border border-primaryB-200/60 px-4 py-3`}>
                      <p className="text-primaryB-900 text-sm font-bold">{feature.highlight}</p>
                      <p className="text-primaryB-300 text-[11px]">{feature.highlightLabel}</p>
                    </div>
                  </div>
                </R>
              </div>
            </div>
          </section>
        );
      })}

      {/* ━━━ ADDITIONAL FEATURES GRID ━━━ */}
      <section className="py-16 sm:py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <R>
            <div className="max-w-lg mb-10 sm:mb-14">
              <p className="text-emerald-600 text-xs font-bold tracking-widest uppercase mb-3">And more</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primaryB-900 tracking-tight mb-3">
                Every tool, included.
              </h2>
              <p className="text-primaryB-500 text-base sm:text-lg">
                Every plan comes loaded with tools to help your business grow.
              </p>
            </div>
          </R>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {additionalFeatures.map((feature, idx) => {
              const FIcon = feature.icon;
              return (
                <R key={feature.title} delay={idx * 50}>
                  <div className="bg-white rounded-2xl p-5 sm:p-6 border border-primaryB-200/60 hover:shadow-card hover:border-primaryB-300/80 transition-all duration-200 group">
                    <div className={`w-10 h-10 rounded-xl ${feature.accent} flex items-center justify-center mb-3.5 transition-transform duration-200 group-hover:scale-110`}>
                      <FIcon weight="bold" className={`w-5 h-5 ${feature.iconColor}`} />
                    </div>
                    <h3 className="text-primaryB-900 font-bold text-sm mb-1.5">{feature.title}</h3>
                    <p className="text-primaryB-400 text-xs sm:text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </R>
              );
            })}
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default FeaturesPage;
